<script>
import * as d3 from 'd3'
import _ from 'lodash'

import {
  default_number_format,
  default_time_format
} from '@/utils/format'

import {
  AxisNames,
  DomainTypes,
  ScaleTypes
} from '@/components/plots/enums'

const scale_map = {}
scale_map[ScaleTypes.Linear] = d3.scaleLinear
scale_map[ScaleTypes.SquareRoot] = d3.scaleSqrt
scale_map[ScaleTypes.Power] = d3.scalePow
scale_map[ScaleTypes.Log] = d3.scaleLog
scale_map[ScaleTypes.Band] = d3.scaleBand
scale_map[ScaleTypes.TimeSeries] = d3.scaleTime

export default {
  computed: {
    scale() {
      let extent = [Infinity, -Infinity]
      const s = (scale_map[this.scale_type] || d3.scaleLinear)()
      switch(this.scale_type) {
        case ScaleTypes.Band:
          extent = [...this.index]
          if(this.reversed) extent.reverse()
          return s.domain(extent)
            .rangeRound(this.range)
            .paddingInner(this.padding)
        case ScaleTypes.TimeSeries:
          this.data.forEach((d, i) => {
            extent[0] = d3.min([extent[0], new Date(d3.min(d))])
            extent[1] = d3.max([extent[1], new Date(d3.max(d))])
          })
          return s.domain(extent)
            .range(this.range)
        default:
          switch(this.domain_type) {
            case DomainTypes.MinMax:
              const d = this.data.flatMap(d => d)
              extent[0] = d3.min(d)
              extent[1] = d3.max(d)
              break
            case DomainTypes.RowSum:
              extent[0] = 0
              extent[1] = this.data.reduce((acc, row) => {
                return Math.max(acc, d3.sum(row))
              }, -Infinity)
              break
          }

          if(this.scale_type === ScaleTypes.Log) {
            extent[0] = Math.max(extent[0], 0.1)
            s.nice()
          }

          const r = this.reversed ? [...this.range].reverse() : this.range
          return s.domain(extent).range(r).interpolate(d3.interpolateRound).nice()
      }
    },
    tick_values() {
      if(!this.scale.ticks) return this.scale.domain()
      const ticks = this.scale.ticks(this.tick_count)
      const n = ticks.length
      if(n <= this.tick_count + 1) return ticks
      const k = Math.floor(n / this.tick_count)
      return ticks.filter((x, i) => i % k === 0)
    }
  },
  data() {
    return {
      is_mounted: false,
      range: [0, 1],
      size: 0,
      tick_count: 5,
      widest_tick_label: null,
      widest_tick_label_bounds: {
        x: 0,
        y: 0,
        width: 0,
        height: 0
      },
      x: 0,
      y: 0
    }
  },
  methods: {
    at(d) {
      switch(this.scale_type) {
        case ScaleTypes.TimeSeries:
          return this.scale(this.create_date(d))
        default:
          return this.scale(d)
      }
    },
    create_date: _.memoize(d => {
      return new Date(d)
    }),
    format(t) {
      switch(this.scale_type) {
        case ScaleTypes.TimeSeries:
          if(this.tick_format) return d3.timeFormat(this.tick_format)(t)
          return default_time_format(t)
        case ScaleTypes.Band:
          if(this.tick_format) return d3.format(this.tick_format)(t)
          return t
        default:
          if(this.tick_format) return d3.format(this.tick_format)(t)
          return default_number_format(t)
      }
    },
    get_size() { return this.size },
    measure_str(s) {
      return new Promise(resolve => {
        const container = this.$refs.container
        const ns = container.namespaceURI
        const el = document.createElementNS(ns, 'text')
        el.innerHTML = s
        container.appendChild(el)
        resolve(el.getBoundingClientRect())
        el.remove()
      })
    },
    async update() {
      if(!this.$refs.title) return

      const widest = this.tick_values.reduce(
        (acc, t) => {
          const formatted = this.format(t)
          const fl = String(formatted).length
          return fl > acc.length ? formatted : acc
        }, '')

      this.widest_tick_label = widest
      this.widest_tick_label_bounds = await this.measure_str(widest)

      const title_bounds = this.$refs.title.getBBox()
      const tick_label_bounds = this.widest_tick_label_bounds
      switch(this.$vnode.data.slot) {
        case AxisNames.Y:
        case AxisNames.Y2:
          this.size =
            title_bounds.height +
            this.tick_size +
            this.tick_label_margin.left +
            this.tick_label_margin.right +
            tick_label_bounds.width
          break
        case AxisNames.X:
        case AxisNames.X2:
          this.size =
            title_bounds.height +
            this.tick_size +
            this.tick_label_margin.top +
            this.tick_label_margin.bottom +
            tick_label_bounds.height
          break
      }
    }
  },
  mounted() {
    switch(this.$vnode.data.slot) {
      case AxisNames.Y:
        this.title_alignment = () => 'hanging'
        this.title_text_anchor = () => 'middle'
        this.title_transform = () => `rotate(-90)`
        this.title_writing_mode = () => 'vertical-tb'
        this.title_y = () => 0
        this.title_x = () =>
          -(this.scale.range()[0] - this.scale.range()[1]) * 0.5
        this.tick_x_1 = (v) => 0
        this.tick_x_2 = (v) => -this.tick_size
        this.tick_y_1 = (v) =>
          Math.floor(this.scale(v) +
            (this.scale.bandwidth ? this.scale.bandwidth() * 0.5 : 0))
        this.tick_y_2 = this.tick_y_1
        this.tick_transform = () => `translate(${this.size} 0)`
        this.tick_label_alignment = () => 'middle'
        this.tick_label_transform = () =>
          `translate(${this.size - this.tick_size - this.tick_label_margin.right} 0)`
        this.tick_label_y = this.tick_y_1
        this.tick_label_x = (v) => 0
        this.tick_label_text_anchor = () => 'end'
        this.x_offset = 0
        this.y_offset = 0
        break

      case AxisNames.Y2:
        this.title_alignment = () => 'hanging'
        this.title_text_anchor = () => 'middle'
        this.title_transform = () => `rotate(90)`
        this.title_writing_mode = () => 'horizontal-tb'
        this.title_y = () => -this.size
        this.title_x = () =>
          -(this.scale.range()[1] - this.scale.range()[0]) * 0.5
        this.tick_x_1 = (v) => 0
        this.tick_x_2 = (v) => this.tick_size
        this.tick_y_1 = (v) =>
          Math.floor(this.scale(v) +
            (this.scale.bandwidth ? this.scale.bandwidth() * 0.5 : 0))
        this.tick_y_2 = this.tick_y_1
        this.tick_transform = () => `translate(0 0)`
        this.tick_label_alignment = () => 'middle'
        this.tick_label_transform = () =>
          `translate(${this.tick_size + this.tick_label_margin.left} 0)`
        this.tick_label_y = this.tick_y_1
        this.tick_label_x = (v) => 0
        this.tick_label_text_anchor = () => 'start'
        this.x_offset = -1
        this.y_offset = 0
        break

      case AxisNames.X:
        this.title_alignment = () => 'baseline'
        this.title_text_anchor = () => 'middle'
        this.title_transform = () => null
        this.title_writing_mode = () => 'horizontal-tb'
        this.title_x = () =>
          this.scale.range()[1] +
          (this.scale.range()[0] - this.scale.range()[1]) * 0.5
        this.title_y = () => this.size
        this.tick_x_1 = (v) =>
          Math.floor(this.scale(v) +
            (this.scale.bandwidth ? this.scale.bandwidth() * 0.5 : 0))
        this.tick_x_2 = this.tick_x_1
        this.tick_y_1 = (v) => 0
        this.tick_y_2 = (v) => this.tick_size
        this.tick_transform = () => `translate(0 0)`
        this.tick_label_alignment = () => 'hanging'
        this.tick_label_transform = () =>
          `translate(0 ${this.tick_size + this.tick_label_margin.top})`
        this.tick_label_y = (v) => 0
        this.tick_label_x = this.tick_x_1
        this.tick_label_text_anchor = () => 'middle'
        this.x_offset = 0
        this.y_offset = 0
        break

      case AxisNames.X2:
        this.title_alignment = () => 'hanging'
        this.title_text_anchor = () => 'middle'
        this.title_transform = null
        this.title_writing_mode = () => 'horizontal-tb'
        this.title_x = () =>
          this.scale.range()[1] +
          (this.scale.range()[0] - this.scale.range()[1]) * 0.5
        this.title_y = () => 0
        this.tick_x_1 = (v) =>
          Math.floor(this.scale(v) +
            (this.scale.bandwidth ? this.scale.bandwidth() * 0.5 : 0))
        this.tick_x_2 = this.tick_x_1
        this.tick_y_1 = (v) => 0
        this.tick_y_2 = (v) => -this.tick_size
        this.tick_transform = () => `translate(0 ${this.size})`
        this.tick_label_alignment = () => 'end'
        this.tick_label_transform = () =>
          `translate(0 ${this.size - this.tick_size - this.tick_label_margin.bottom})`
        this.tick_label_y = (v) => 0
        this.tick_label_x = this.tick_x_1
        this.tick_label_text_anchor = () => 'middle'
        this.x_offset = 0
        this.y_offset = 0
        break
    }

    this.is_mounted = true
  },
  name: 'Axis',
  props: {
    index: {type: Array, default: () => null},
    padding: {type: Number, default: () => 0.1},
    domain_type: {type: String, default: () => DomainTypes.MinMax},
    reversed: {type: Boolean, default: () => false},
    scale_type: {type: String, default: () => ScaleTypes.Linear},
    tick_format: {type: String, default: null},
    tick_label_margin: {
      type: Object,
      default: () => ({
        left: 5,
        right: 5,
        top: 5,
        bottom: 5
      })
    },
    tick_size: {type: Number, default: () => 5},
    title: {type: String, default: () => ''},
    data: {type: Array, default: () => [[]]}
  }
}
</script>

<template>
<g ref='container'>
  <g :transform='`translate(
    ${Math.floor(x) + this.x_offset}
    ${Math.floor(y) + this.y_offset})`' v-if='is_mounted'>

    <text ref='title' :text-anchor='title_text_anchor()'
      :y='title_y()' :x='title_x()'
      :transform='title_transform ? title_transform() : false'
      :alignment-baseline='title_alignment()'
      :writing-mode='title_writing_mode()' dx='0.65em' class='axis-label overline'>
      {{ title }}
    </text>

    <g :transform='tick_label_transform()'>
      <text v-for='(t, i) in tick_values' :key='i'
        :y='tick_label_y(t)' :x='tick_label_x(t)'
        :text-anchor='tick_label_text_anchor()'
        :alignment-baseline='tick_label_alignment()'
        class='tick-label'>
        {{format(t)}}
      </text>
    </g>

    <g :transform='tick_transform()' >
      <line v-for='(t, i) in tick_values' :key='i'
        :x1='tick_x_1(t)' :x2='tick_x_2(t)'
        :y1='tick_y_1(t)' :y2='tick_y_2(t)'
        class='tick-mark'/>
    </g>
  </g>
</g>
</template>

<style lang='sass' scoped>
.axis-label, .tick-label
  font-size: 0.80rem !important

.theme--dark
  .axis-label
    fill: var(--v-grey-lighten5)
  .tick-label
    fill: var(--v-grey-lighten5)
  .tick-mark
    stroke: var(--v-grey-darken6)

.theme--light
  .axis-label
    fill: var(--v-grey-darken4)
  .tick-label
    fill: var(--v-grey-darken4)
  .tick-mark
    stroke: var(--v-grey-darken2)
</style>
